export const ATTENDANCE_INSIGHTS_KEY = "attendanceActionInsightsLog";
export const LEAVE_REQUEST_ACTION = "Leave Request";

export function loadAttendanceInsights() {
  try {
    const raw = localStorage.getItem(ATTENDANCE_INSIGHTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveAttendanceInsights(items) {
  try {
    localStorage.setItem(ATTENDANCE_INSIGHTS_KEY, JSON.stringify(items));
  } catch {
    /* ignore */
  }
}

export function mapActionLabelToInsightAction(label) {
  const t = String(label || "").trim();
  if (t === "Leave Request") return "Leave";
  return t || "General";
}

function isAttendanceRequestAction(label) {
  const action = mapActionLabelToInsightAction(label);
  return [
    LEAVE_REQUEST_ACTION,
    "Leave",
    "Change Shift",
    "Change WO",
    "Regularise checks",
    "Short Leave",
    "Status change request",
    "On Duty",
    "Comp Off",
    "Comp Off Work",
    "Comp Off Credit",
    "Overtime",
    "Overtime",
    "Restricted Holiday",
  ].includes(action);
}

function resolveEmployeeCode(employee, employeeCode) {
  const code =
    employeeCode ||
    employee?.empCode ||
    employee?.emp_code ||
    employee?.employeeCode ||
    employee?.employee_code ||
    null;
  if (code != null && String(code).trim()) return String(code).trim();
  return "-";
}

function buildAttendanceRequestInsight({
  id,
  requestId,
  actionLabel,
  employee,
  employeeCode,
  employeeName,
  employeeId,
  formData = {},
  pendingAt = "Reporting Manager",
  status = "Pending",
}) {
  const code = resolveEmployeeCode(employee, employeeCode);
  const name =
    employeeName ||
    employee?.user?.name ||
    employee?.name ||
    "-";
  return {
    id: id || `att_req_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    requestId: requestId || null,
    action: mapActionLabelToInsightAction(actionLabel),
    employeeCode: code,
    employeeName: name,
    employeeId: employee?.id ?? employeeId ?? null,
    status,
    pendingAt,
    submittedAt: new Date().toLocaleString(),
    formData: {
      ...formData,
      employeeCode: formData?.employeeCode || code,
      employeeName: formData?.employeeName || name,
    },
  };
}

export function buildLeaveRequestInsight({
  id,
  requestId,
  employee,
  employeeCode,
  employeeName,
  employeeId,
  formData = {},
  pendingAt = "Reporting Manager",
  status = "Pending",
}) {
  return buildAttendanceRequestInsight({
    id: id || `leave_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    requestId,
    actionLabel: "Leave",
    employee,
    employeeCode,
    employeeName,
    employeeId,
    pendingAt,
    status,
    formData: {
      leaveType: formData.leaveType || formData.leaveStatus || "CL",
      leaveDuration: formData.leaveDuration || "full_day",
      fromDate: formData.fromDate || "",
      toDate: formData.toDate || "",
      reason: formData.reason || formData.leaveReason || "",
      notes: formData.notes || "",
      employeeCode: formData.employeeCode || employeeCode || "",
      employeeName: formData.employeeName || employeeName || "",
    },
  });
}

export function appendLeaveRequestInsight(entry) {
  const next = [entry, ...loadAttendanceInsights()];
  saveAttendanceInsights(next);
  return next;
}

export function updateAttendanceInsight(id, patch) {
  const items = loadAttendanceInsights();
  const next = items.map((row) => (row.id === id ? { ...row, ...patch } : row));
  saveAttendanceInsights(next);
  return next;
}

export function removeAttendanceInsight(id) {
  const next = loadAttendanceInsights().filter((row) => row.id !== id);
  saveAttendanceInsights(next);
  return next;
}

export function mapQuickActionRequestToInsight(request, employeeLookup) {
  const fd = request?.formData || {};
  const employee =
    typeof employeeLookup === "function"
      ? employeeLookup(request)
      : employeeLookup || null;

  return buildAttendanceRequestInsight({
    id: `att_req_${request.requestId}`,
    requestId: request.requestId,
    actionLabel: request.actionLabel,
    employee,
    employeeCode: request.employeeCode || fd.employeeCode,
    employeeId: request.employeeId,
    employeeName: request.userName,
    formData: fd,
    pendingAt: request.pendingWith || "Reporting Manager",
    status: request.status || "Pending",
  });
}

export function syncQuickActionLeaveRequestsToInsights(quickActionRequests, employeeResolver) {
  const existing = loadAttendanceInsights();
  const byRequestId = new Set(
    existing.map((r) => r.requestId).filter(Boolean)
  );
  const requestRows = (quickActionRequests || []).filter(
    (r) => isAttendanceRequestAction(r?.actionLabel)
  );
  const toAdd = requestRows
    .filter((r) => r.requestId && !byRequestId.has(r.requestId))
    .map((r) => mapQuickActionRequestToInsight(r, employeeResolver));
  if (!toAdd.length) return existing;
  const merged = [...toAdd, ...existing];
  saveAttendanceInsights(merged);
  return merged;
}
